package com.bulut.mecitogullari.ui.screens.transactions

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.util.YearMonth
import com.bulut.mecitogullari.core.util.currentYearMonth
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.data.model.MonthSummary
import com.bulut.mecitogullari.data.model.balance
import com.bulut.mecitogullari.data.model.computeMonthSummary
import com.bulut.mecitogullari.data.model.inMonth
import com.bulut.mecitogullari.data.model.ledgerOnly
import com.bulut.mecitogullari.data.repository.DebtRepository
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.TransactionRepository
import com.bulut.mecitogullari.ui.excel.ExcelImportController
import com.bulut.mecitogullari.ui.excel.ExcelImportState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update

enum class TransactionsTab { ALL, INCOME, EXPENSE }

data class TransactionsUiState(
    val month: YearMonth = currentYearMonth(),
    val tab: TransactionsTab = TransactionsTab.ALL,
    val query: String = "",
    val transactions: List<CashTransaction> = emptyList(),
    val projectNames: Map<String, String> = emptyMap(),
    /** Devir + gelir/gider — aylık PDF raporu bunu basar. */
    val monthSummary: MonthSummary = MonthSummary(currentYearMonth()),
    /** The month's entries with no tab/search filter applied. */
    val monthTransactions: List<CashTransaction> = emptyList(),
    /** Sum of open supplier balances — shown on the Borç Takibi shortcut. */
    val totalSupplierDebt: Double = 0.0,
)

class TransactionsViewModel(
    transactionRepository: TransactionRepository,
    projectRepository: ProjectRepository,
    debtRepository: DebtRepository,
) : ViewModel() {

    private val filters = MutableStateFlow(TransactionsUiState())

    /** Ay bazlı .xlsx içe aktarma: proje sütunu isimle eşleştirilir. */
    private val excelImport = ExcelImportController(
        scope = viewModelScope,
        transactionRepository = transactionRepository,
        projectRepository = projectRepository,
    )
    val excelImportState: StateFlow<ExcelImportState> = excelImport.state

    /** Tarih yazmayan satırlar görüntülenen ayın 1'ine yazılır. */
    fun startExcelImport() = excelImport.start(
        fixedProjectId = null,
        fixedProjectName = null,
        defaultDateEpochMillis = filters.value.month.startEpochMillis(),
    )

    fun confirmExcelImport() = excelImport.confirm()

    fun dismissExcelImport() = excelImport.dismiss()

    private val supplierDebt = combine(
        debtRepository.suppliers,
        debtRepository.entries,
    ) { suppliers, entries ->
        val supplierIds = suppliers
            .filter { it.kind == AccountKind.SUPPLIER }
            .map { it.id }
            .toSet()
        entries.filter { it.supplierId in supplierIds }
            .groupBy { it.supplierId }
            .values
            .sumOf { it.balance().coerceAtLeast(0.0) }
    }

    val uiState: StateFlow<TransactionsUiState> = combine(
        transactionRepository.transactions,
        projectRepository.projects,
        supplierDebt,
        filters,
    ) { transactions, projects, totalDebt, filter ->
        val inMonth = transactions.ledgerOnly().inMonth(filter.month)
        val byTab = when (filter.tab) {
            TransactionsTab.ALL -> inMonth
            TransactionsTab.INCOME -> inMonth.filter { it.type == TransactionType.INCOME }
            TransactionsTab.EXPENSE -> inMonth.filter { it.type == TransactionType.EXPENSE }
        }
        val query = filter.query.trim()
        val visible = if (query.isBlank()) byTab else byTab.filter {
            it.description.contains(query, ignoreCase = true) ||
                it.category.contains(query, ignoreCase = true)
        }
        filter.copy(
            transactions = visible.sortedByDescending { it.dateEpochMillis },
            projectNames = projects.associate { it.id to it.name },
            monthSummary = computeMonthSummary(transactions, filter.month),
            monthTransactions = inMonth.sortedByDescending { it.dateEpochMillis },
            totalSupplierDebt = totalDebt,
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = TransactionsUiState(),
    )

    fun onTabChange(tab: TransactionsTab) = filters.update { it.copy(tab = tab) }

    fun onQueryChange(query: String) = filters.update { it.copy(query = query) }

    fun onPreviousMonth() = filters.update { it.copy(month = it.month.previous()) }

    fun onNextMonth() = filters.update { it.copy(month = it.month.next()) }
}
